// src/cockpit4/components/HumanMachineManagement.tsx
// ============================================
// 人机协作管理（总控台 P2-功能10）- v2.0 详细改进计划
// 清晰划分 AI 做/人做的事 / AI 与人任务清单 / 协作效率分析 / 最佳实践推荐
// 数据来源：localStorage（xeaos_human_machine）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Bot, User, Clock, TrendingUp, RefreshCw, Sparkles } from "lucide-react";

interface TaskAllocation {
  id: string;
  task: string;
  owner: "AI" | "人" | "协作";
  timeSaved: number;
  freq: string;
  status: "运行中" | "已分配" | "待确认";
}

const INITIAL_TASKS: TaskAllocation[] = [
  { id: "t1", task: "每日晨报生成", owner: "AI", timeSaved: 30, freq: "每日", status: "运行中" },
  { id: "t2", task: "营销内容初稿", owner: "AI", timeSaved: 120, freq: "每日", status: "运行中" },
  { id: "t3", task: "客户跟进记录", owner: "AI", timeSaved: 45, freq: "实时", status: "运行中" },
  { id: "t4", task: "财务对账与凭证", owner: "AI", timeSaved: 90, freq: "每日", status: "运行中" },
  { id: "t5", task: "战略决策与拍板", owner: "人", timeSaved: 0, freq: "每周", status: "已分配" },
  { id: "t6", task: "客户高层拜访", owner: "人", timeSaved: 0, freq: "每周", status: "已分配" },
  { id: "t7", task: "AI 方案审核与优化", owner: "协作", timeSaved: 20, freq: "每日", status: "待确认" },
  { id: "t8", task: "跨部门资源协调", owner: "协作", timeSaved: 15, freq: "每周", status: "待确认" },
];

const KEY = "xeaos_human_machine";

function load(): TaskAllocation[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as TaskAllocation[];
  } catch { /* ignore */ }
  return INITIAL_TASKS;
}

export default function HumanMachineManagement() {
  const [tasks, setTasks] = useState<TaskAllocation[]>(load);

  const stats = useMemo(() => {
    const aiTasks = tasks.filter((t) => t.owner === "AI").length;
    const humanTasks = tasks.filter((t) => t.owner === "人").length;
    const totalSaved = tasks.reduce((a, t) => a + t.timeSaved, 0);
    const weeklySaved = totalSaved * 5;
    return { aiTasks, humanTasks, weeklySaved };
  }, [tasks]);

  const aiRatio = useMemo(() => Math.round((stats.aiTasks / tasks.length) * 100), [stats.aiTasks, tasks.length]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Bot size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">人机协作管理</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Human-AI</span>
        <button
          onClick={() => setTasks((prev) => prev.map((t) => (t.timeSaved > 0 ? { ...t, timeSaved: t.timeSaved + 5 } : t)))}
          className="flex items-center gap-1 ml-auto px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white"
        >
          <RefreshCw size={10} /> 模拟刷新
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="AI 承担" value={`${stats.aiTasks} 项`} color="#34D399" />
        <MetricCard label="人类核心" value={`${stats.humanTasks} 项`} color="#38BDF8" />
        <MetricCard label="AI 占比" value={`${aiRatio}%`} color="#A78BFA" />
        <MetricCard label="每周节省" value={`${stats.weeklySaved} 分钟`} color="#FBBF24" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">任务分工 · AI / 人 / 协作</span>
          </div>
          <div className="space-y-1.5">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                {t.owner === "AI" ? <Bot size={11} className="text-emerald-400 flex-shrink-0" /> : t.owner === "人" ? <User size={11} className="text-sky-400 flex-shrink-0" /> : <Sparkles size={11} className="text-amber-400 flex-shrink-0" />}
                <span className="text-[10px] text-white">{t.task}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  t.owner === "AI" ? "bg-emerald-500/15 text-emerald-400" : t.owner === "人" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"
                }`}>{t.owner}</span>
                <span className="text-[8px] font-mono text-gray-600">{t.freq}</span>
                {t.timeSaved > 0 && <span className="text-[9px] font-mono text-emerald-400 ml-auto">节省 {t.timeSaved}min</span>}
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  t.status === "运行中" ? "bg-emerald-500/15 text-emerald-400" : t.status === "已分配" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"
                }`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">协作效率分析</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400">📈 AI 已替代 55% 的重复性工作，人类聚焦决策与关系</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-emerald-400">⏱ 本周累计为团队节省 {stats.weeklySaved} 分钟（约 {(stats.weeklySaved / 60).toFixed(1)} 小时）</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400">💡 最佳实践：晨报由 AI 生成初稿 → 老板 5 分钟审核定稿，兼顾效率与质量</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-cyan-400">🔄 建议：将「周报数据汇总」也交由 AI，进一步释放人力</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

